import React from "react";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Container,
  Grid,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import ArchitectureIcon from "@mui/icons-material/Architecture";
import SettingsIcon from "@mui/icons-material/Settings";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import HubIcon from "@mui/icons-material/Hub";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import CloudQueueIcon from "@mui/icons-material/CloudQueue";
import PageTitle from "../components/common/PageTitle";
import {
  getAccessibleAgents,
  type AgentCardConfig,
} from "../utils/agentDashboardConfig";
import { getDecodedToken } from "../utils/jwtUtils";

const agentIcons: Record<string, React.ReactNode> = {
  architect: <ArchitectureIcon sx={{ fontSize: 40, color: "primary.main" }} />,
  "catalog-assistant": (
    <SmartToyIcon sx={{ fontSize: 40, color: "primary.main" }} />
  ),
  operations: <SettingsIcon sx={{ fontSize: 40, color: "primary.main" }} />,
  "integration-analyst": (
    <HubIcon sx={{ fontSize: 40, color: "primary.main" }} />
  ),
  "customer-support": (
    <SupportAgentIcon sx={{ fontSize: 40, color: "primary.main" }} />
  ),
  devops: <CloudQueueIcon sx={{ fontSize: 40, color: "primary.main" }} />,
};

function AgentCard({ agent }: { agent: AgentCardConfig }) {
  const enabled = Boolean(agent.route) && !agent.comingSoon;
  const cardBody = (
      <CardContent sx={{ textAlign: "center", py: 3 }}>
        {agentIcons[agent.id] ?? (
          <SmartToyOutlinedIcon sx={{ fontSize: 40, color: "primary.main" }} />
        )}
        <Typography variant="h6" fontWeight={600} sx={{ mt: 2 }}>
          {agent.title}
        </Typography>
        <Chip
          label={enabled ? "Available" : "Coming soon"}
          size="small"
          color={enabled ? "success" : "default"}
          sx={{ mt: 1, mb: 0.5 }}
        />
        <Typography variant="caption" color="text.secondary" display="block">
          {agent.access.label}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          {agent.description}
        </Typography>
        {enabled ? (
          <Chip label="Open workspace" size="small" color="primary" sx={{ mt: 2 }} />
        ) : (
          <Chip
            label="Coming soon"
            size="small"
            variant="outlined"
            sx={{ mt: 2 }}
          />
        )}
      </CardContent>
  );

  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        borderRadius: 2,
        transition: "box-shadow 0.2s",
        "&:hover": { boxShadow: enabled ? 4 : 1 },
      }}
    >
      {enabled ? (
        <CardActionArea component={RouterLink} to={agent.route!}>
          {cardBody}
        </CardActionArea>
      ) : (
        cardBody
      )}
    </Card>
  );
}

const AgentDashboardPage: React.FC = () => {
  const decoded = getDecodedToken();
  const userRoles = decoded?.roles ?? [];
  const visibleAgents = getAccessibleAgents(userRoles);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <PageTitle
        pageTitle="Agents"
        pageDescription="Specialized AI agents for architecture, operations, integration, and support."
      />

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {visibleAgents.length === 0 ? (
          <Typography color="text.secondary" textAlign="center">
            No agents are available for your account. Contact an administrator if you
            need access.
          </Typography>
        ) : (
          <Grid container spacing={3}>
            {visibleAgents.map((agent) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={agent.id}>
                <AgentCard agent={agent} />
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default AgentDashboardPage;
